import { useState } from 'react';
import { getCityTierRates, type TradeType, PROVIDER_TIER_RATES_CONFIG } from '../../../config/providerTierRates';

interface Trade {
  id: string;
  title: string;
  tag: string;
  subtitle: string;
  icon: string;
  typicalEarnings: string;
  bullets: string[];
  requirements: string[];
  cta: string;
}

interface TradeSelectionProps {
  selectedTrade: string | null;
  onSelectTrade: (tradeId: string) => void;
}

export default function TradeSelection({ selectedTrade, onSelectTrade }: TradeSelectionProps) {
  const [showTierModal, setShowTierModal] = useState(false);
  const [selectedCity, setSelectedCity] = useState<string>('');

  const tradeOptions: Trade[] = [
    {
      id: 'handyman',
      title: 'Handyman Services',
      tag: 'High demand · Quick jobs',
      subtitle: 'Repairs, installs & general maintenance.',
      icon: 'ri-tools-fill',
      typicalEarnings: '£34–£46.75 / hour (after 15% platform fee)',
      bullets: [
        'Shelving, mounting, assembly',
        'Small repairs & snagging',
        'Property maintenance tasks'
      ],
      requirements: [
        'Tools + experience',
        'Public liability insurance'
      ],
      cta: 'Apply as Handyman'
    },
    {
      id: 'plumbing',
      title: 'Plumbing Services',
      tag: 'Higher value · Urgent work',
      subtitle: 'Repairs, installs & emergency call-outs.',
      icon: 'ri-drop-fill',
      typicalEarnings: '£51–£68 / hour (after 15% platform fee)',
      bullets: [
        'Leaks, taps, toilets, pipework',
        'Appliance installs',
        'Emergency call-outs'
      ],
      requirements: [
        'Plumbing experience',
        'Public liability insurance'
      ],
      cta: 'Apply as Plumber'
    },
    {
      id: 'electrical',
      title: 'Electrical Services',
      tag: 'Skilled work · Strong rates',
      subtitle: 'Fault finding, installs & safety repairs.',
      icon: 'ri-flashlight-fill',
      typicalEarnings: '£46.75–£63.75 / hour (after 15% platform fee)',
      bullets: [
        'Fault finding & repairs',
        'Sockets, lighting, switches',
        'Safety-related jobs'
      ],
      requirements: [
        'Electrical qualifications (where required)',
        'Public liability insurance'
      ],
      cta: 'Apply as Electrician'
    },
    {
      id: 'cleaning',
      title: 'Cleaning Services',
      tag: 'Consistent bookings · Repeat work',
      subtitle: 'Regular, deep & end-of-tenancy cleaning.',
      icon: 'ri-brush-fill',
      typicalEarnings: '£25.50–£29.75 / hour (after 15% platform fee)',
      bullets: [
        'Home cleaning & deep cleans',
        'End-of-tenancy cleans',
        'Office cleaning (optional)'
      ],
      requirements: [
        'Professional standard + reliability',
        'Public liability insurance (recommended)'
      ],
      cta: 'Apply as Cleaner'
    },
    {
      id: 'transportation',
      title: 'Transportation & Moving',
      tag: 'Nationwide pricing · Van required',
      subtitle: 'Deliveries, moves & multi-drop jobs.',
      icon: 'ri-truck-fill',
      typicalEarnings: 'Example: 10 miles (Small van) ≈ £55.25 earned (after 15% platform fee)',
      bullets: [
        'Single item / furniture delivery',
        'Home & office moves',
        'Multi-drop deliveries'
      ],
      requirements: [
        'Own van + valid insurance',
        'Valid driving licence'
      ],
      cta: 'Apply for Transport'
    },
    {
      id: 'waste',
      title: 'Waste Removal',
      tag: 'Fast jobs · Strong demand',
      subtitle: 'Clearances, renovation waste & bulky items.',
      icon: 'ri-delete-bin-fill',
      typicalEarnings: 'Example: ¼ load job ≈ £119 earned (after 15% platform fee)',
      bullets: [
        'House clearances & bulky waste',
        'Renovation/rubble removal',
        'Garden waste removal'
      ],
      requirements: [
        'Waste carrier licence',
        'Suitable vehicle + insurance'
      ],
      cta: 'Apply for Waste Removal'
    }
  ];

  const handleApplyNow = (tradeId: string) => {
    onSelectTrade(tradeId);
  };

  const handleViewTier = (citySlug: string) => {
    console.log('View Tier clicked:', { citySlug, selectedTrade });
    if (!selectedTrade) {
      alert('Please select a trade first to view tier details');
      return;
    }
    setSelectedCity(citySlug);
    setShowTierModal(true);
  };

  const getTierModalData = () => {
    console.log('Getting tier modal data:', { selectedCity, selectedTrade });
    if (!selectedCity || !selectedTrade) {
      console.log('Missing city or trade');
      return null;
    }

    // Map trade IDs to TradeType
    const tradeTypeMap: Record<string, TradeType> = {
      'handyman': 'handyman',
      'plumbing': 'plumbing',
      'electrical': 'electrical',
      'cleaning': 'cleaning'
    };

    const tradeType = tradeTypeMap[selectedTrade];
    console.log('Mapped trade type:', tradeType);
    if (!tradeType) {
      console.log('Invalid trade type');
      return null;
    }

    const tierData = getCityTierRates(selectedCity, tradeType);
    console.log('Tier data:', tierData);
    if (!tierData) {
      console.log('No tier data found');
      return null;
    }

    // Get trade display name
    const tradeNames: Record<string, string> = {
      'handyman': 'Handyman',
      'plumbing': 'Plumbing',
      'electrical': 'Electrical',
      'cleaning': 'Cleaning'
    };

    const result = {
      ...tierData,
      tradeName: tradeNames[selectedTrade] || selectedTrade
    };
    console.log('Final modal data:', result);
    return result;
  };

  const modalData = getTierModalData();

  return (
    <div className="bg-gradient-to-br from-gray-50 to-gray-100 px-4 py-12">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#0B3C5D] to-[#328BA8] rounded-2xl mb-6 shadow-lg">
            <i className="ri-tools-line text-3xl text-white"></i>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Choose Your Trade
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Select the service you'll provide on Taskbudy. You can add more trades later from your dashboard.
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="max-w-4xl mx-auto mb-8">
          <div className="bg-gradient-to-r from-[#0B3C5D] to-[#328BA8] rounded-xl p-6 border border-[#328BA8]/30">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white text-[#0B3C5D] rounded-full flex items-center justify-center font-bold">
                  1
                </div>
                <div>
                  <h3 className="font-bold text-white">Step 1 of 5</h3>
                  <p className="text-sm text-white/90">Choose your trade</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-white/90">Estimated time</p>
                <p className="font-bold text-white">2 minutes</p>
              </div>
            </div>
            <div className="w-full bg-white/30 rounded-full h-2">
              <div className="bg-white rounded-full h-2 w-1/5"></div>
            </div>
          </div>
        </div>

        {/* Subscription Info Card - REMOVED */}

        {/* Trade Selection Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
          {tradeOptions.map((trade) => (
            <div
              key={trade.id}
              className={`relative cursor-pointer rounded-xl border-2 transition-all duration-300 bg-white hover:shadow-lg flex flex-col ${
                selectedTrade === trade.id
                  ? 'border-[#0B3C5D] shadow-lg ring-2 ring-[#0B3C5D]/20'
                  : 'border-gray-200 hover:border-[#328BA8]'
              }`}
              onClick={() => handleApplyNow(trade.id)}
            >
              {selectedTrade === trade.id && (
                <div className="absolute -top-3 -right-3 w-8 h-8 bg-[#0B3C5D] rounded-full flex items-center justify-center shadow-lg z-10">
                  <i className="ri-check-line text-lg text-white"></i>
                </div>
              )}

              <div className="p-6 flex flex-col flex-1">
                {/* Icon & Title */}
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#0B3C5D] to-[#328BA8] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                    <i className={`${trade.icon} text-xl text-white`}></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900 mb-1">
                      {trade.title}
                    </h3>
                    <div className="inline-flex items-center gap-1 text-xs text-[#328BA8] font-medium">
                      <i className="ri-star-fill"></i>
                      <span>{trade.tag}</span>
                    </div>
                  </div>
                </div>

                {/* Subtitle */}
                <p className="text-sm text-gray-600 mb-4">
                  {trade.subtitle}
                </p>

                {/* Typical Earnings */}
                <div className="bg-gradient-to-r from-[#0B3C5D]/5 to-[#328BA8]/5 rounded-lg p-3 mb-4 border border-[#328BA8]/20">
                  <p className="text-xs text-gray-600 mb-1 font-medium">Typical earnings</p>
                  <p className="text-sm font-bold text-[#0B3C5D]">
                    {trade.typicalEarnings}
                  </p>
                </div>

                {/* What you'll do */}
                <div className="mb-4">
                  <p className="text-xs font-semibold text-gray-700 mb-2">What you'll do:</p>
                  <ul className="space-y-1.5">
                    {trade.bullets.map((bullet, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-xs text-gray-600">
                        <i className="ri-check-line text-[#328BA8] mt-0.5 flex-shrink-0"></i>
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Requirements */}
                <div className="mb-4 pb-4 border-b border-gray-200">
                  <p className="text-xs font-semibold text-gray-700 mb-2">Requirements:</p>
                  <ul className="space-y-1.5">
                    {trade.requirements.map((req, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-xs text-gray-600">
                        <i className="ri-shield-check-line text-[#0B3C5D] mt-0.5 flex-shrink-0"></i>
                        <span>{req}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA Button - pushed to bottom with mt-auto */}
                <div className="mt-auto">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleApplyNow(trade.id);
                    }}
                    className="w-full bg-gradient-to-r from-[#0B3C5D] to-[#328BA8] text-white py-3 px-4 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 whitespace-nowrap text-sm"
                  >
                    {trade.cta}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Tier Details Modal */}
      {showTierModal && modalData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            {/* Modal Header */}
            <div className="sticky top-0 bg-gradient-to-r from-[#0B3C5D] to-[#328BA8] text-white p-6 rounded-t-2xl">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-2xl font-bold mb-1">{modalData.tierLabel}</h2>
                  <p className="text-white/90 text-sm">
                    {modalData.cityName} - {modalData.tradeName}
                  </p>
                </div>
                <button
                  onClick={() => setShowTierModal(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-4">
              {/* Normal Hours */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-5 border-2 border-gray-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gray-600 rounded-xl flex items-center justify-center">
                    <i className="ri-sun-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Normal Hours</h3>
                    <p className="text-sm text-gray-600">6:00 AM - 6:00 PM</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white rounded-lg p-3 border border-gray-200">
                    <p className="text-xs text-gray-600 mb-1">First Hour</p>
                    <p className="text-2xl font-bold text-gray-900">£{modalData.normalRates.firstHour}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-gray-200">
                    <p className="text-xs text-gray-600 mb-1">Per 30 Minutes</p>
                    <p className="text-2xl font-bold text-gray-900">£{modalData.normalRates.halfHour}</p>
                  </div>
                </div>
              </div>

              {/* After Hours */}
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-5 border-2 border-indigo-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center">
                    <i className="ri-moon-line text-2xl text-white"></i>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-bold text-gray-900">After Hours</h3>
                      <span className="bg-indigo-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                        +20%
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">6:00 PM - 6:00 AM</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white rounded-lg p-3 border border-indigo-200">
                    <p className="text-xs text-gray-600 mb-1">First Hour</p>
                    <p className="text-2xl font-bold text-indigo-600">£{modalData.afterHoursRates.firstHour}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-indigo-200">
                    <p className="text-xs text-gray-600 mb-1">Per 30 Minutes</p>
                    <p className="text-2xl font-bold text-indigo-600">£{modalData.afterHoursRates.halfHour}</p>
                  </div>
                </div>
              </div>

              {/* Late Call-Out Fee */}
              <div className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-xl p-5 border-2 border-rose-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-rose-600 rounded-xl flex items-center justify-center">
                    <i className="ri-alarm-warning-line text-2xl text-white"></i>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-bold text-gray-900">Late Call-Out Fee</h3>
                      <span className="bg-rose-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                        +£{modalData.lateCalloutFee}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">Emergency call-outs after 8:00 PM</p>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4 border border-rose-200">
                  <p className="text-3xl font-bold text-rose-600 mb-1">£{modalData.lateCalloutFee}</p>
                  <p className="text-xs text-gray-600">Additional fee for short-notice after-hours jobs</p>
                </div>
              </div>

              {/* Info Box */}
              <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-lg p-4 border border-teal-200">
                <div className="flex items-start gap-3">
                  <i className="ri-information-line text-teal-600 text-xl mt-0.5"></i>
                  <div className="text-sm text-gray-700">
                    <p className="font-semibold text-gray-900 mb-2">How You Get Paid</p>
                    <ul className="space-y-1.5">
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-teal-600 mt-0.5"></i>
                        <span>Platform fee: 15% deducted from all earnings</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-teal-600 mt-0.5"></i>
                        <span>After-hours premium automatically applied for jobs between 6pm-6am</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-teal-600 mt-0.5"></i>
                        <span>Late call-out fee added for emergency jobs accepted after 8pm</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-50 p-6 rounded-b-2xl border-t border-gray-200">
              <button
                onClick={() => setShowTierModal(false)}
                className="w-full bg-gradient-to-r from-[#0B3C5D] to-[#328BA8] text-white py-3 px-6 rounded-lg font-semibold hover:shadow-lg transition-all whitespace-nowrap"
              >
                Got It
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
